import React, { useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import classNames from 'classnames';
import type { Column } from 'react-table';
import { FiCheck, FiX } from '@vyaguta/vyaguta-icons/fi';

import PopOver from 'components/common/popover';
import Loading from 'components/common/loading/Loading';
import { CustomCheckbox } from 'components/common/checkbox';
import TableHeader from 'components/common/table/TableHeader';
import AvatarLinkComponent from 'components/common/avatar/AvatarLinkComponent';

import { interpolate } from 'utils/string';
import { getFormattedDate } from 'utils/date';

import { LEAVE_ISSUER_CHANGE_STATUS } from 'constants/changeLeaveIssuer';
import { EMPLOYEE_PROFILE } from 'constants/routes';
import { DISPLAY_DATE_FORMAT } from 'constants/date';
import { LEAVE_ISSUER_CHANGE_REASON_LENGTH, LEAVE_ISSUER_CHANGE_USER_NAME_LENGTH } from 'constants/string';

import type { LeaveIssuerChangeRequestItem } from 'interface/issuer';
import type { ServiceId } from 'interface/service';

interface LeaveIssuerActionCellProps {
  row: LeaveIssuerChangeRequestItem;
  isRejectingRequest: boolean;
  onApprove: (
    user: LeaveIssuerChangeRequestItem['user'],
    status: string,
    newLeaveIssuer: LeaveIssuerChangeRequestItem['newLeaveIssuer'],
    setIsApprovingRequest: Dispatch<SetStateAction<boolean>>
  ) => Promise<void>;
  onReject: (row: LeaveIssuerChangeRequestItem) => void;
}

interface ColumnCellProps {
  original: LeaveIssuerChangeRequestItem;
}

interface GetChangeLeaveIssuerColumnsProps {
  selectedRequestIds: ServiceId[];
  setSelectedRequestIds: Dispatch<SetStateAction<ServiceId[]>>;
  setIsSelectAllChecked: Dispatch<SetStateAction<boolean>>;
  isAscendingOrdered: boolean;
  setIsAscendingOrdered: Dispatch<SetStateAction<boolean>>;
  columnToSortBy: string;
  setColumnToSortBy: Dispatch<SetStateAction<string>>;
  isRejectingRequest: boolean;
  handleApproveClick: LeaveIssuerActionCellProps['onApprove'];
  openRejectModal: (row: LeaveIssuerChangeRequestItem) => void;
}

function LeaveIssuerActionCell({ row, isRejectingRequest, onApprove, onReject }: LeaveIssuerActionCellProps) {
  const { user, status, newLeaveIssuer, comment } = row;
  const [isApprovingRequest, setIsApprovingRequest] = useState(false);

  if (status === LEAVE_ISSUER_CHANGE_STATUS.REJECTED) {
    return (
      <PopOver
        interactive
        html={
          <div className="tooltip-info word-break">
            <p className="tooltip-info__title">
              <span className="font-weight-bold">Reason:</span> {comment}
            </p>
          </div>
        }
        visible={Boolean(comment)}
      >
        <div className="leave-issuer-list__approved">{status}</div>
      </PopOver>
    );
  }

  if (status === LEAVE_ISSUER_CHANGE_STATUS.APPROVED) {
    return <div className="leave-issuer-list__approved">{status}</div>;
  }

  return (
    <div className="d-flex gap-px-8">
      <button
        className={classNames('leave-issuer-list__button leave-issuer-list__button--approve center', {
          'leave-issuer-list__button--approve-loading': isApprovingRequest,
        })}
        onClick={() => onApprove(user, LEAVE_ISSUER_CHANGE_STATUS.APPROVED, newLeaveIssuer, setIsApprovingRequest)}
        disabled={isApprovingRequest || isRejectingRequest}
      >
        {isApprovingRequest ? <Loading className="p-absolute white spinner-small" /> : <FiCheck size={16} />}
      </button>
      <button
        className={classNames('leave-issuer-list__button leave-issuer-list__button--reject center', {
          'leave-issuer-list__button--reject-loading': isRejectingRequest,
        })}
        onClick={() => onReject(row)}
        disabled={isRejectingRequest || isApprovingRequest}
      >
        {isRejectingRequest ? <Loading className="p-absolute white spinner-small" /> : <FiX size={16} />}
      </button>
    </div>
  );
}

export function getChangeLeaveIssuerColumns({
  selectedRequestIds,
  setSelectedRequestIds,
  setIsSelectAllChecked,
  isAscendingOrdered,
  setIsAscendingOrdered,
  columnToSortBy,
  setColumnToSortBy,
  isRejectingRequest,
  handleApproveClick,
  openRejectModal,
}: GetChangeLeaveIssuerColumnsProps): Array<Column<LeaveIssuerChangeRequestItem>> {
  return [
    {
      Header: <TableHeader label="" identifier="checkbox" />,
      sortable: false,
      filterable: false,
      maxWidth: 20,
      resizable: false,
      className: 'cursor-default p-16',
      Cell: ({ original: { id: currentId, status } }: ColumnCellProps) => {
        if (status !== LEAVE_ISSUER_CHANGE_STATUS.PENDING) {
          return (
            <CustomCheckbox
              disabled
              className="p-absolute opacity-0 request-checkbox staffing-request-checkbox cursor-not-allowed mb-20"
            />
          );
        }

        return (
          <CustomCheckbox
            className="p-absolute opacity-0 request-checkbox staffing-request-checkbox mb-20"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              e.stopPropagation();

              const uncheckCheckbox = selectedRequestIds.includes(currentId);

              setSelectedRequestIds((prevValue) => {
                if (uncheckCheckbox) {
                  setIsSelectAllChecked(false);

                  return prevValue.filter((id) => id !== currentId);
                }

                return [...prevValue, currentId];
              });
            }}
            checked={selectedRequestIds.includes(currentId)}
          />
        );
      },
    },
    {
      Header: <TableHeader label="Employee" identifier="employee" />,
      sortable: false,
      filterable: false,
      width: 190,
      className: 'cursor-default p-16',
      Cell: ({ original: { user } }: ColumnCellProps) => (
        <PopOver
          interactive
          html={
            <div className="tooltip-info word-break">
              <p className="tooltip-info__title">{user.fullname}</p>
            </div>
          }
          visible={user.fullname.length > LEAVE_ISSUER_CHANGE_USER_NAME_LENGTH}
        >
          <AvatarLinkComponent customClass="ellipsis" empId={user.empId} id={user.id} name={user.fullname} />
        </PopOver>
      ),
    },
    {
      Header: <TableHeader label="Requested By" identifier="requestedBy" />,
      sortable: false,
      filterable: false,
      maxWidth: 125,
      className: 'cursor-default p-16',
      Cell: ({ original: { requestedBy, createdByRole } }: ColumnCellProps) => (
        <div className="d-flex flex-direction-column">
          <PopOver
            interactive
            html={
              <div className="tooltip-info word-break">
                <p className="tooltip-info__title">{requestedBy.fullname}</p>
              </div>
            }
            visible={requestedBy.fullname.length > LEAVE_ISSUER_CHANGE_USER_NAME_LENGTH}
          >
            <a
              className="d-flex align-items-center"
              href={interpolate(EMPLOYEE_PROFILE, { id: requestedBy.id })}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              <span className="ellipsis">{requestedBy.fullname}</span>
            </a>
          </PopOver>
          <span className="font-size-10">{createdByRole}</span>
        </div>
      ),
    },
    {
      Header: <TableHeader label="Requested To" identifier="requestedTo" />,
      sortable: false,
      filterable: false,
      maxWidth: 125,
      className: 'cursor-default p-16',
      Cell: ({ original: { newLeaveIssuer, newLeaveIssuerRole } }: ColumnCellProps) => (
        <div className="d-flex flex-direction-column">
          <PopOver
            interactive
            html={
              <div className="tooltip-info word-break">
                <p className="tooltip-info__title">{newLeaveIssuer.fullname}</p>
              </div>
            }
            visible={newLeaveIssuer.fullname.length > LEAVE_ISSUER_CHANGE_USER_NAME_LENGTH}
          >
            <a
              className="d-flex align-items-center"
              href={interpolate(EMPLOYEE_PROFILE, { id: newLeaveIssuer.id })}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              <span className="ellipsis">{newLeaveIssuer.fullname}</span>
            </a>
          </PopOver>
          <span className="font-size-10">{newLeaveIssuerRole}</span>
        </div>
      ),
    },
    {
      Header: <TableHeader label="Issuer Count" identifier="count" />,
      sortable: false,
      filterable: false,
      accessor: 'issueCount',
      maxWidth: 120,
      className: 'cursor-default p-16',
      Cell: ({ original: { issueCount } }: ColumnCellProps) => <div>{issueCount}</div>,
    },
    {
      Header: (
        <TableHeader
          label="Date of Request"
          identifier="dateOfRequest"
          columnToSortBy={columnToSortBy}
          isAscendingOrdered={isAscendingOrdered}
          onClick={() => {
            setIsAscendingOrdered((prevStatus) => columnToSortBy !== 'dateOfRequest' || !prevStatus);
            setColumnToSortBy('dateOfRequest');
          }}
        />
      ),
      accessor: 'createdAt',
      maxWidth: 200,
      className: 'cursor-default p-16',
      Cell: ({ original }: ColumnCellProps) => <div>{getFormattedDate(original.createdAt, DISPLAY_DATE_FORMAT)}</div>,
    },
    {
      Header: (
        <TableHeader
          label="Transition Date"
          identifier="transitionDate"
          columnToSortBy={columnToSortBy}
          isAscendingOrdered={isAscendingOrdered}
          onClick={() => {
            setIsAscendingOrdered((prevStatus) => columnToSortBy !== 'transitionDate' || !prevStatus);
            setColumnToSortBy('transitionDate');
          }}
        />
      ),
      accessor: 'transitionDate',
      maxWidth: 150,
      className: 'cursor-default p-16',
      Cell: ({ original }: ColumnCellProps) => (
        <div>{getFormattedDate(original.transitionDate, DISPLAY_DATE_FORMAT)}</div>
      ),
    },
    {
      Header: <TableHeader label="Reason" identifier="reason" />,
      accessor: 'reason',
      maxWidth: 120,
      sortable: false,
      filterable: false,
      className: 'cursor-default p-16',
      Cell: ({ original: { reason } }: ColumnCellProps) => (
        <PopOver
          interactive
          html={
            <div className="tooltip-info word-break">
              <p className="tooltip-info__title">{reason}</p>
            </div>
          }
          visible={reason.length > LEAVE_ISSUER_CHANGE_REASON_LENGTH}
        >
          <div className="ellipsis">{reason}</div>
        </PopOver>
      ),
    },
    {
      Header: <TableHeader label="Status" identifier="status" />,
      maxWidth: 110,
      sortable: false,
      filterable: false,
      className: 'cursor-default p-16',
      Cell: ({ original: { status } }: ColumnCellProps) => (
        <div className="d-flex gap-px-8 align-items-center">
          <div
            className={classNames('leave-issuer-list__status-bulb flex-shrink-0', {
              [`leave-issuer-list__status-bulb--${status.toLowerCase()}`]: status,
            })}
          ></div>
          <div>{status}</div>
        </div>
      ),
    },
    {
      Header: <TableHeader label="Action" identifier="action" />,
      maxWidth: 120,
      sortable: false,
      filterable: false,
      className: 'cursor-default p-16',
      Cell: ({ original }: ColumnCellProps) => (
        <LeaveIssuerActionCell
          row={original}
          isRejectingRequest={isRejectingRequest}
          onApprove={handleApproveClick}
          onReject={openRejectModal}
        />
      ),
    },
  ];
}
